import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { toast, Toaster } from 'sonner';
import axios from 'axios';
import {
  Globe, Palette, Save, Loader2, Eye, Upload, X, Plus, Trash2,
  GripVertical, ArrowLeft, ExternalLink, Layout, Type, Image as ImageIcon,
  Mail, Phone, Instagram, Facebook, Twitter, Youtube, Music, Copy, Check,
  Link2, Shield, AlertCircle, CheckCircle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${API}/api`;

const TEMPLATES = [
  { id: 'elegant', name: 'Elegant', desc: 'Dark, cinematic — perfect for weddings', preview: 'linear-gradient(135deg, #1a1a2e, #16213e)' },
  { id: 'modern', name: 'Modern', desc: 'Clean, minimal, bold typography', preview: 'linear-gradient(135deg, #0f172a, #1e293b)' },
  { id: 'party', name: 'Party', desc: 'Vibrant, dynamic — clubs & events', preview: 'linear-gradient(135deg, #2d1b69, #1a1a2e)' },
  { id: 'classic', name: 'Classic', desc: 'Traditional, professional layout', preview: 'linear-gradient(135deg, #1c1917, #292524)' },
];

const COLOR_PRESETS = [
  '#e91e63', '#f44336', '#ff5722', '#ff9800', '#ffc107',
  '#4caf50', '#009688', '#00bcd4', '#2196f3', '#3f51b5',
  '#9c27b0', '#673ab7', '#795548', '#607d8b', '#ffffff',
];

const FONT_OPTIONS = [
  { value: 'elegant', label: 'Elegant (Serif)' },
  { value: 'modern', label: 'Modern (Sans)' },
  { value: 'fun', label: 'Fun (Display)' },
  { value: 'classic', label: 'Classic (Georgia)' },
];

const SECTIONS = [
  { id: 'hero', label: 'Hero Banner', required: true },
  { id: 'about', label: 'About' },
  { id: 'packages', label: 'Packages & Pricing' },
  { id: 'addons', label: 'Add-Ons' },
  { id: 'gallery', label: 'Gallery' },
  { id: 'testimonials', label: 'Testimonials' },
  { id: 'availability', label: 'Availability Checker' },
  { id: 'contact', label: 'Contact / Book Now' },
];

export default function WebsiteBuilderPage() {
  const navigate = useNavigate();
  const [ws, setWs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('template');
  const [planTier, setPlanTier] = useState('free');
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const heroRef = useRef(null);
  const logoRef = useRef(null);
  const aboutImgRef = useRef(null);
  const galleryRef = useRef(null);
  const [uploading, setUploading] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const tierRes = await axios.get(`${API_BASE}/plan-tier`, { headers });
      setPlanTier(tierRes.data.plan_tier);
      if (tierRes.data.plan_tier !== 'business') { setLoading(false); return; }

      const res = await axios.get(`${API_BASE}/website-settings`, { headers });
      if (res.data.exists === false) {
        setWs(null);
      } else {
        setWs(res.data);
      }
    } catch (err) {
      if (err.response?.status === 403) setPlanTier('free');
    } finally {
      setLoading(false);
    }
  };

  const createSite = async () => {
    try {
      const res = await axios.post(`${API_BASE}/website-settings`, {}, { headers });
      setWs(res.data);
      toast.success('Website created!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create');
    }
  };

  const save = async (updates) => {
    setSaving(true);
    try {
      const res = await axios.put(`${API_BASE}/website-settings`, updates, { headers });
      setWs(res.data);
      toast.success('Saved!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const update = (field, value) => setWs(prev => ({ ...prev, [field]: value }));

  const uploadImage = async (file, field) => {
    setUploading(prev => ({ ...prev, [field]: true }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', 'website');
      const res = await axios.post(`${API_BASE}/upload`, formData, { headers });
      update(field, res.data.url);
      await save({ [field]: res.data.url });
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(prev => ({ ...prev, [field]: false }));
    }
  };

  const addGalleryImage = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const file of files) {
      setUploading(prev => ({ ...prev, gallery: true }));
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('folder', 'website-gallery');
        const res = await axios.post(`${API_BASE}/upload`, formData, { headers });
        const newImages = [...(ws.gallery_images || []), { url: res.data.url, caption: '' }];
        update('gallery_images', newImages);
        await save({ gallery_images: newImages });
      } catch { toast.error('Upload failed'); }
      finally { setUploading(prev => ({ ...prev, gallery: false })); }
    }
    if (galleryRef.current) galleryRef.current.value = '';
  };

  const removeGalleryImage = (index) => {
    const newImages = (ws.gallery_images || []).filter((_, i) => i !== index);
    update('gallery_images', newImages);
    save({ gallery_images: newImages });
  };

  const addTestimonial = () => {
    const newT = [...(ws.testimonials || []), { name: '', text: '', event_type: '' }];
    update('testimonials', newT);
  };

  const updateTestimonial = (index, field, value) => {
    const newT = [...(ws.testimonials || [])];
    newT[index] = { ...newT[index], [field]: value };
    update('testimonials', newT);
  };

  const removeTestimonial = (index) => {
    const newT = (ws.testimonials || []).filter((_, i) => i !== index);
    update('testimonials', newT);
    save({ testimonials: newT });
  };

  const toggleSection = (sectionId, enabled) => {
    const config = { ...(ws.sections_config || {}) };
    config[sectionId] = { ...config[sectionId], enabled };
    update('sections_config', config);
    save({ sections_config: config });
  };

  const siteUrl = ws ? `${window.location.origin}/site/${ws.slug}` : '';

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f]">
      <Loader2 className="w-8 h-8 animate-spin text-white/30" />
    </div>
  );

  // Plan gate
  if (planTier !== 'business') return (
    <div className="min-h-screen bg-[#0a0a0f] text-white flex items-center justify-center px-4">
      <Toaster position="top-right" theme="dark" />
      <div className="max-w-md text-center space-y-6">
        <Globe className="w-16 h-16 text-white/15 mx-auto" />
        <h1 className="text-3xl font-bold">Website Hosting</h1>
        <p className="text-white/50">Host a professional website for your DJ business with custom templates, packages, and a booking form.</p>
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6 text-left space-y-3">
          <p className="text-white font-semibold">Business Plan Required</p>
          <ul className="text-white/50 text-sm space-y-1.5">
            <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-400" />Custom website with 4 template styles</li>
            <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-400" />Display packages, add-ons & pricing</li>
            <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-400" />Photo gallery & testimonials</li>
            <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-400" />Contact form → leads pipeline</li>
            <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-400" />Email forwarding (yourname@djcrm.net)</li>
          </ul>
        </div>
        <Button onClick={() => navigate('/settings')} variant="outline" className="border-white/20 text-white">
          <ArrowLeft className="w-4 h-4 mr-2" />Back to Settings
        </Button>
      </div>
    </div>
  );

  // No site yet
  if (!ws) return (
    <div className="min-h-screen bg-[#0a0a0f] text-white flex items-center justify-center px-4">
      <Toaster position="top-right" theme="dark" />
      <div className="max-w-md text-center space-y-6">
        <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 flex items-center justify-center">
          <Globe className="w-10 h-10 text-blue-400" />
        </div>
        <h1 className="text-3xl font-bold">Create Your Website</h1>
        <p className="text-white/50">Build a professional website for your DJ business. Choose a template, customize colors, and showcase your packages.</p>
        <Button onClick={createSite} className="bg-blue-500 hover:bg-blue-600 rounded-xl px-8 h-12 text-base font-semibold" data-testid="create-website-btn">
          <Globe className="w-5 h-5 mr-2" />Get Started
        </Button>
      </div>
    </div>
  );

  const verifyDomain = async () => {
    if (!site.custom_domain) { toast.error('Enter a domain first'); return; }
    setVerifying(true);
    setVerifyResult(null);
    try {
      const res = await axios.post(`${API}/website-settings/verify-domain`, { domain: site.custom_domain }, { headers });
      setVerifyResult(res.data);
      if (res.data.verified) toast.success('Domain verified!');
      else toast.error('Domain verification failed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  const copyText = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied!');
  };

  const tabs = [
    { id: 'template', label: 'Template', icon: Layout },
    { id: 'branding', label: 'Branding', icon: Palette },
    { id: 'content', label: 'Content', icon: Type },
    { id: 'gallery', label: 'Gallery', icon: ImageIcon },
    { id: 'sections', label: 'Sections', icon: GripVertical },
    { id: 'domain', label: 'Domain', icon: Globe },
    { id: 'email', label: 'Email', icon: Mail },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white" data-testid="website-builder">
      <Toaster position="top-right" theme="dark" />

      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#0a0a0f]/95 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/settings')} className="p-2 rounded-xl hover:bg-white/10" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5 text-white/60" />
            </button>
            <div>
              <h1 className="text-lg font-bold">Website Builder</h1>
              <p className="text-white/40 text-xs">{siteUrl}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 mr-3">
              <span className="text-white/40 text-xs">Published</span>
              <Switch
                checked={ws.is_published}
                onCheckedChange={v => { update('is_published', v); save({ is_published: v }); }}
                data-testid="publish-toggle"
              />
            </div>
            {ws.is_published && (
              <a href={siteUrl} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="sm" className="border-white/20 text-white bg-white/5 hover:bg-white/10 rounded-xl" data-testid="preview-site-btn">
                  <Eye className="w-4 h-4 mr-1.5" />Preview
                </Button>
              </a>
            )}
            <Button size="sm" onClick={() => { navigator.clipboard.writeText(siteUrl); toast.success('Link copied!'); }} variant="outline" className="border-white/20 text-white bg-white/5 hover:bg-white/10 rounded-xl">
              <Copy className="w-4 h-4 mr-1.5" />Copy Link
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-4">
        {/* Tabs */}
        <div className="flex gap-1 bg-white/5 p-1 rounded-xl mb-4 overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 py-2 px-3 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                activeTab === tab.id ? 'bg-white/15 text-white' : 'text-white/40 hover:text-white/60'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />{tab.label}
            </button>
          ))}
        </div>

        {/* Template Tab */}
        {activeTab === 'template' && (
          <div className="space-y-4" data-testid="template-tab">
            <h2 className="text-white font-semibold">Choose Your Template</h2>
            <div className="grid grid-cols-2 gap-4">
              {TEMPLATES.map(t => (
                <button
                  key={t.id}
                  onClick={() => { update('template', t.id); save({ template: t.id }); }}
                  className={`rounded-2xl border-2 overflow-hidden transition-all text-left ${
                    ws.template === t.id ? 'border-blue-500 ring-2 ring-blue-500/30' : 'border-white/10 hover:border-white/20'
                  }`}
                  data-testid={`template-${t.id}`}
                >
                  <div className="h-32 w-full" style={{ background: t.preview }} />
                  <div className="p-3 bg-white/5">
                    <p className="text-white font-semibold text-sm">{t.name}</p>
                    <p className="text-white/40 text-xs">{t.desc}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Branding Tab */}
        {activeTab === 'branding' && (
          <div className="space-y-4" data-testid="branding-tab">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Title + Tagline */}
              <div className="bg-white/5 rounded-2xl border border-white/10 p-4 space-y-3">
                <h3 className="text-white font-semibold text-sm">Site Title & Tagline</h3>
                <div><Label className="text-white/60 text-xs">Site Title</Label>
                  <Input value={ws.site_title || ''} onChange={e => update('site_title', e.target.value)} className="mt-1 bg-white/10 border-white/15 text-white rounded-xl" data-testid="site-title-input" />
                </div>
                <div><Label className="text-white/60 text-xs">Tagline</Label>
                  <Input value={ws.tagline || ''} onChange={e => update('tagline', e.target.value)} className="mt-1 bg-white/10 border-white/15 text-white rounded-xl" data-testid="tagline-input" />
                </div>
                <div><Label className="text-white/60 text-xs">URL Slug</Label>
                  <Input value={ws.slug || ''} onChange={e => update('slug', e.target.value)} className="mt-1 bg-white/10 border-white/15 text-white rounded-xl font-mono" data-testid="slug-input" />
                  <p className="text-white/30 text-xs mt-1">{window.location.origin}/site/{ws.slug}</p>
                </div>
              </div>
              {/* Logo + Hero */}
              <div className="bg-white/5 rounded-2xl border border-white/10 p-4 space-y-3">
                <h3 className="text-white font-semibold text-sm">Images</h3>
                {[
                  { label: 'Logo', field: 'logo_url', ref: logoRef },
                  { label: 'Hero Image', field: 'hero_image_url', ref: heroRef },
                ].map(({ label, field, ref }) => (
                  <div key={field} className="flex items-center gap-3">
                    {ws[field] ? (
                      <div className="relative w-16 h-16 rounded-lg overflow-hidden bg-white/5 border border-white/10 shrink-0">
                        <img src={ws[field].startsWith('/') ? `${API}${ws[field]}` : ws[field]} alt="" className="w-full h-full object-cover" />
                        <button onClick={() => { update(field, ''); save({ [field]: '' }); }} className="absolute -top-1 -right-1 p-0.5 bg-red-500 rounded-full"><X className="w-3 h-3 text-white" /></button>
                      </div>
                    ) : (
                      <div className="w-16 h-16 rounded-lg bg-white/5 border border-dashed border-white/20 flex items-center justify-center shrink-0">
                        <ImageIcon className="w-6 h-6 text-white/20" />
                      </div>
                    )}
                    <div>
                      <Button variant="outline" size="sm" onClick={() => ref.current?.click()} disabled={uploading[field]} className="border-white/20 text-white bg-white/5 hover:bg-white/10 rounded-xl" data-testid={`upload-${field}-btn`}>
                        {uploading[field] ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}{label}
                      </Button>
                      <input ref={ref} type="file" accept="image/*" onChange={e => e.target.files?.[0] && uploadImage(e.target.files[0], field)} className="hidden" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {/* Colors */}
            <div className="bg-white/5 rounded-2xl border border-white/10 p-4 space-y-4">
              <h3 className="text-white font-semibold text-sm">Colors</h3>
              {[
                { label: 'Primary', field: 'primary_color' },
                { label: 'Secondary (Background)', field: 'secondary_color' },
                { label: 'Accent', field: 'accent_color' },
              ].map(({ label, field }) => (
                <div key={field}>
                  <Label className="text-white/60 text-xs">{label}</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {COLOR_PRESETS.map(c => (
                      <button key={c} onClick={() => update(field, c)}
                        className={`w-7 h-7 rounded-full border-2 transition-transform ${ws[field] === c ? 'scale-125 border-white' : 'border-transparent hover:scale-110'}`}
                        style={{ background: c }} data-testid={`${field}-${c.replace('#','')}`} />
                    ))}
                    <Input type="color" value={ws[field] || '#000'} onChange={e => update(field, e.target.value)} className="w-7 h-7 p-0 border-0 rounded-full cursor-pointer" />
                  </div>
                </div>
              ))}
            </div>
            {/* Font */}
            <div className="bg-white/5 rounded-2xl border border-white/10 p-4 space-y-3">
              <h3 className="text-white font-semibold text-sm">Font Style</h3>
              <div className="flex gap-2">
                {FONT_OPTIONS.map(f => (
                  <button key={f.value} onClick={() => update('font_style', f.value)}
                    className={`flex-1 py-3 rounded-xl border text-sm transition-all ${ws.font_style === f.value ? 'border-white bg-white/15 text-white' : 'border-white/10 text-white/40'}`}
                    data-testid={`font-${f.value}`}>{f.label}</button>
                ))}
              </div>
            </div>
            {/* Social Links */}
            <div className="bg-white/5 rounded-2xl border border-white/10 p-4 space-y-3">
              <h3 className="text-white font-semibold text-sm">Social Links</h3>
              {[
                { key: 'instagram', icon: Instagram, placeholder: 'Instagram URL' },
                { key: 'facebook', icon: Facebook, placeholder: 'Facebook URL' },
                { key: 'youtube', icon: Youtube, placeholder: 'YouTube URL' },
                { key: 'tiktok', icon: Music, placeholder: 'TikTok URL' },
              ].map(({ key, icon: Icon, placeholder }) => (
                <div key={key} className="flex items-center gap-2">
                  <Icon className="w-4 h-4 text-white/40 shrink-0" />
                  <Input value={(ws.social_links || {})[key] || ''} placeholder={placeholder}
                    onChange={e => update('social_links', { ...(ws.social_links || {}), [key]: e.target.value })}
                    className="bg-white/10 border-white/15 text-white rounded-xl text-sm" data-testid={`social-${key}`} />
                </div>
              ))}
            </div>
            <Button onClick={() => save({ site_title: ws.site_title, tagline: ws.tagline, slug: ws.slug, primary_color: ws.primary_color, secondary_color: ws.secondary_color, accent_color: ws.accent_color, font_style: ws.font_style, social_links: ws.social_links })}
              disabled={saving} className="w-full h-12 rounded-xl font-semibold bg-blue-500 hover:bg-blue-600" data-testid="save-branding-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Save Branding
            </Button>
          </div>
        )}

        {/* Content Tab */}
        {activeTab === 'content' && (
          <div className="space-y-4" data-testid="content-tab">
            <div className="bg-white/5 rounded-2xl border border-white/10 p-4 space-y-3">
              <h3 className="text-white font-semibold text-sm">About Section</h3>
              <Input value={ws.about_title || ''} onChange={e => update('about_title', e.target.value)} placeholder="About Us" className="bg-white/10 border-white/15 text-white rounded-xl" data-testid="about-title-input" />
              <Textarea value={ws.about_content || ''} onChange={e => update('about_content', e.target.value)} placeholder="Tell your story..." rows={5} className="bg-white/10 border-white/15 text-white rounded-xl" data-testid="about-content-input" />
              <div className="flex items-center gap-3">
                {ws.about_image_url ? (
                  <div className="relative w-20 h-20 rounded-lg overflow-hidden bg-white/5">
                    <img src={ws.about_image_url.startsWith('/') ? `${API}${ws.about_image_url}` : ws.about_image_url} alt="" className="w-full h-full object-cover" />
                    <button onClick={() => { update('about_image_url', ''); save({ about_image_url: '' }); }} className="absolute -top-1 -right-1 p-0.5 bg-red-500 rounded-full"><X className="w-3 h-3 text-white" /></button>
                  </div>
                ) : null}
                <Button variant="outline" size="sm" onClick={() => aboutImgRef.current?.click()} className="border-white/20 text-white bg-white/5 rounded-xl" data-testid="upload-about-image-btn">
                  <Upload className="w-4 h-4 mr-1" />About Image
                </Button>
                <input ref={aboutImgRef} type="file" accept="image/*" onChange={e => e.target.files?.[0] && uploadImage(e.target.files[0], 'about_image_url')} className="hidden" />
              </div>
            </div>
            <div className="bg-white/5 rounded-2xl border border-white/10 p-4 space-y-3">
              <h3 className="text-white font-semibold text-sm">Contact Info</h3>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-white/60 text-xs">Contact Email</Label>
                  <Input value={ws.contact_email || ''} onChange={e => update('contact_email', e.target.value)} className="mt-1 bg-white/10 border-white/15 text-white rounded-xl" data-testid="contact-email-input" /></div>
                <div><Label className="text-white/60 text-xs">Contact Phone</Label>
                  <Input value={ws.contact_phone || ''} onChange={e => update('contact_phone', e.target.value)} className="mt-1 bg-white/10 border-white/15 text-white rounded-xl" data-testid="contact-phone-input" /></div>
              </div>
            </div>
            <div className="bg-white/5 rounded-2xl border border-white/10 p-4 space-y-3">
              <h3 className="text-white font-semibold text-sm">SEO</h3>
              <Textarea value={ws.meta_description || ''} onChange={e => update('meta_description', e.target.value)} placeholder="Brief description for search engines..." rows={2} className="bg-white/10 border-white/15 text-white rounded-xl" data-testid="meta-desc-input" />
            </div>
            {/* Testimonials */}
            <div className="bg-white/5 rounded-2xl border border-white/10 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-white font-semibold text-sm">Testimonials</h3>
                <Button variant="outline" size="sm" onClick={addTestimonial} className="border-white/20 text-white bg-white/5 rounded-xl" data-testid="add-testimonial-btn"><Plus className="w-4 h-4 mr-1" />Add</Button>
              </div>
              {(ws.testimonials || []).map((t, i) => (
                <div key={i} className="bg-white/5 rounded-xl p-3 space-y-2 relative">
                  <button onClick={() => removeTestimonial(i)} className="absolute top-2 right-2 p-1 hover:bg-red-500/20 rounded-lg"><Trash2 className="w-4 h-4 text-red-400" /></button>
                  <Input value={t.name || ''} onChange={e => updateTestimonial(i, 'name', e.target.value)} placeholder="Client name" className="bg-white/10 border-white/15 text-white rounded-xl text-sm" />
                  <Textarea value={t.text || ''} onChange={e => updateTestimonial(i, 'text', e.target.value)} placeholder="Their review..." rows={2} className="bg-white/10 border-white/15 text-white rounded-xl text-sm" />
                  <Input value={t.event_type || ''} onChange={e => updateTestimonial(i, 'event_type', e.target.value)} placeholder="Event type (e.g., Wedding)" className="bg-white/10 border-white/15 text-white rounded-xl text-sm" />
                </div>
              ))}
            </div>
            <Button onClick={() => save({ about_title: ws.about_title, about_content: ws.about_content, contact_email: ws.contact_email, contact_phone: ws.contact_phone, meta_description: ws.meta_description, testimonials: ws.testimonials })}
              disabled={saving} className="w-full h-12 rounded-xl font-semibold bg-blue-500 hover:bg-blue-600" data-testid="save-content-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Save Content
            </Button>
          </div>
        )}

        {/* Gallery Tab */}
        {activeTab === 'gallery' && (
          <div className="space-y-4" data-testid="gallery-tab">
            <div className="flex items-center justify-between">
              <h2 className="text-white font-semibold">{(ws.gallery_images || []).length} Photos</h2>
              <Button variant="outline" size="sm" onClick={() => galleryRef.current?.click()} disabled={uploading.gallery} className="border-white/20 text-white bg-white/5 rounded-xl" data-testid="add-gallery-btn">
                {uploading.gallery ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}Add Photos
              </Button>
              <input ref={galleryRef} type="file" accept="image/*" multiple onChange={addGalleryImage} className="hidden" />
            </div>
            {(ws.gallery_images || []).length === 0 ? (
              <div className="text-center py-16 bg-white/5 rounded-2xl border border-dashed border-white/10">
                <ImageIcon className="w-12 h-12 text-white/10 mx-auto mb-3" />
                <p className="text-white/30 text-sm">Upload photos to showcase your events</p>
              </div>
            ) : (
              <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                {(ws.gallery_images || []).map((img, i) => (
                  <div key={i} className="aspect-square rounded-xl overflow-hidden bg-white/5 relative group border border-white/10">
                    <img src={img.url?.startsWith('/') ? `${API}${img.url}` : img.url} alt="" className="w-full h-full object-cover" />
                    <button onClick={() => removeGalleryImage(i)} className="absolute top-1 right-1 p-1 bg-red-500/80 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-gallery-${i}`}>
                      <X className="w-3 h-3 text-white" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Sections Tab */}
        {activeTab === 'sections' && (
          <div className="space-y-3" data-testid="sections-tab">
            <h2 className="text-white font-semibold">Toggle Sections</h2>
            <p className="text-white/40 text-sm">Choose which sections appear on your website.</p>
            {SECTIONS.map(s => {
              const config = (ws.sections_config || {})[s.id] || { enabled: true };
              return (
                <div key={s.id} className="bg-white/5 rounded-xl border border-white/10 p-4 flex items-center justify-between">
                  <span className="text-white text-sm font-medium">{s.label}</span>
                  <Switch
                    checked={config.enabled}
                    onCheckedChange={v => toggleSection(s.id, v)}
                    disabled={s.required}
                    data-testid={`toggle-${s.id}`}
                  />
                </div>
              );
            })}
          </div>
        )}

        {/* Domain Tab */}
        {activeTab === 'domain' && (
          <div className="space-y-5" data-testid="domain-tab">
            {/* Subdomain */}
            <div className="bg-white/5 rounded-2xl border border-white/10 p-5 space-y-4">
              <h3 className="text-white font-semibold text-sm flex items-center gap-2">
                <Globe className="w-4 h-4 text-green-400" />
                Subdomain (Free)
              </h3>
              <p className="text-white/40 text-xs">Your website is automatically available at a djcrm.net subdomain based on your URL slug.</p>
              <div className="flex items-center gap-2 bg-white/[0.03] rounded-xl p-3 border border-white/10">
                <div className="flex-1 flex items-center gap-1.5">
                  <span className="text-white font-mono text-sm">{ws.slug}</span>
                  <span className="text-white/30 font-mono text-sm">.djcrm.net</span>
                </div>
                <Button size="sm" variant="outline" className="border-white/20 text-white bg-white/5 hover:bg-white/10 rounded-lg h-8"
                  onClick={() => copyText(`${ws.slug}.djcrm.net`)}
                >
                  <Copy className="w-3.5 h-3.5 mr-1" /> Copy
                </Button>
              </div>
              {ws.is_published && (
                <div className="flex items-center gap-2 text-xs text-green-400">
                  <CheckCircle className="w-3.5 h-3.5" /> Live and accessible
                </div>
              )}
            </div>

            {/* Custom Domain */}
            <div className="bg-white/5 rounded-2xl border border-white/10 p-5 space-y-4">
              <h3 className="text-white font-semibold text-sm flex items-center gap-2">
                <Link2 className="w-4 h-4 text-amber-400" />
                Custom Domain
              </h3>
              <p className="text-white/40 text-xs">Use your own domain name (e.g., www.yourdomain.com) to point to your DJ CRM website.</p>
              
              <div className="space-y-2">
                <Label className="text-white/60 text-xs">Your Domain</Label>
                <div className="flex items-center gap-2">
                  <Input
                    value={ws.custom_domain || ''}
                    onChange={e => update('custom_domain', e.target.value.toLowerCase().trim())}
                    placeholder="www.yourdomain.com"
                    className="bg-white/10 border-white/15 text-white rounded-xl flex-1"
                    data-testid="custom-domain-input"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-white/20 text-white bg-white/5 hover:bg-white/10 rounded-xl h-10 px-4"
                    onClick={verifyDomain}
                    disabled={verifying || !ws.custom_domain}
                    data-testid="verify-domain-btn"
                  >
                    {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4 mr-1" />}
                    Verify
                  </Button>
                </div>
              </div>

              {/* DNS Instructions */}
              <div className="bg-white/[0.03] rounded-xl border border-white/10 p-4 space-y-3">
                <h4 className="text-white/70 text-xs font-semibold uppercase tracking-wider">DNS Setup Instructions</h4>
                <p className="text-white/40 text-xs">Add the following DNS record at your domain registrar:</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-white/30 border-b border-white/10">
                        <th className="text-left py-2 pr-4">Type</th>
                        <th className="text-left py-2 pr-4">Name/Host</th>
                        <th className="text-left py-2 pr-4">Value/Target</th>
                        <th className="text-left py-2">TTL</th>
                      </tr>
                    </thead>
                    <tbody className="text-white/60">
                      <tr className="border-b border-white/5">
                        <td className="py-2 pr-4 font-mono">CNAME</td>
                        <td className="py-2 pr-4 font-mono">
                          {ws.custom_domain?.startsWith('www.') ? 'www' : '@'}
                        </td>
                        <td className="py-2 pr-4">
                          <div className="flex items-center gap-2">
                            <span className="font-mono">sites.djcrm.net</span>
                            <button onClick={() => copyText('sites.djcrm.net')} className="text-white/30 hover:text-white">
                              <Copy className="w-3 h-3" />
                            </button>
                          </div>
                        </td>
                        <td className="py-2 font-mono">3600</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-white/30 text-xs">DNS changes can take up to 24-48 hours to propagate. After adding the record, click "Verify" to check.</p>
              </div>

              {/* Verification Results */}
              {verifyResult && (
                <div className="space-y-2">
                  {verifyResult.checks.map((check, i) => (
                    <div key={i} className={`flex items-start gap-2 p-3 rounded-xl text-xs ${check.passed ? 'bg-green-500/10 border border-green-500/20' : 'bg-red-500/10 border border-red-500/20'}`}>
                      {check.passed ? <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 shrink-0" /> : <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />}
                      <div>
                        <p className={check.passed ? 'text-green-400 font-medium' : 'text-red-400 font-medium'}>{check.type}</p>
                        <p className="text-white/50 mt-0.5">{check.message}</p>
                      </div>
                    </div>
                  ))}
                  {verifyResult.verified && (
                    <div className="flex items-center gap-2 text-xs text-green-400 mt-2">
                      <CheckCircle className="w-4 h-4" /> Custom domain verified and active!
                    </div>
                  )}
                </div>
              )}

              {ws.custom_domain_verified && !verifyResult && (
                <div className="flex items-center gap-2 text-xs text-green-400">
                  <CheckCircle className="w-3.5 h-3.5" /> Domain verified and active
                </div>
              )}
            </div>

            {/* How it works info */}
            <div className="bg-blue-500/10 rounded-2xl border border-blue-500/20 p-4">
              <h4 className="text-blue-300 text-xs font-semibold mb-2 flex items-center gap-2">
                <AlertCircle className="w-3.5 h-3.5" /> How Domain Routing Works
              </h4>
              <ul className="space-y-1.5 text-xs text-blue-200/60">
                <li>Your site is always available at <strong className="text-blue-300">{ws.slug}.djcrm.net</strong></li>
                <li>With a custom domain, visitors to your domain see the same site</li>
                <li>Both URLs stay active — no downtime during setup</li>
                <li>SSL certificates are automatically provisioned for custom domains</li>
              </ul>
            </div>
          </div>
        )}

        {/* Email Tab */}
        {activeTab === 'email' && (
          <div className="space-y-4" data-testid="email-tab">
            <div className="bg-white/5 rounded-2xl border border-white/10 p-4 space-y-3">
              <h3 className="text-white font-semibold text-sm flex items-center gap-2"><Mail className="w-4 h-4 text-blue-400" />Email Forwarding</h3>
              <p className="text-white/40 text-xs">Create professional email addresses that forward to your real email. E.g., bookings@{ws.slug}.djcrm.net forwards to your Gmail.</p>
              <div className="space-y-2">
                {(ws.email_forwarding || []).map((ef, i) => (
                  <div key={i} className="flex items-center gap-2 bg-white/5 rounded-xl p-2">
                    <Input value={ef.alias || ''} onChange={e => {
                      const newEf = [...(ws.email_forwarding || [])];
                      newEf[i] = { ...newEf[i], alias: e.target.value };
                      update('email_forwarding', newEf);
                    }} placeholder="alias" className="bg-white/10 border-white/15 text-white rounded-lg text-sm w-32" />
                    <span className="text-white/30 text-xs shrink-0">@{ws.slug}.djcrm.net</span>
                    <span className="text-white/20 text-xs shrink-0">→</span>
                    <Input value={ef.forward_to || ''} onChange={e => {
                      const newEf = [...(ws.email_forwarding || [])];
                      newEf[i] = { ...newEf[i], forward_to: e.target.value };
                      update('email_forwarding', newEf);
                    }} placeholder="forward to email" className="bg-white/10 border-white/15 text-white rounded-lg text-sm flex-1" />
                    <button onClick={() => {
                      const newEf = (ws.email_forwarding || []).filter((_, idx) => idx !== i);
                      update('email_forwarding', newEf);
                    }} className="p-1 hover:bg-red-500/20 rounded-lg"><X className="w-4 h-4 text-red-400" /></button>
                  </div>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={() => update('email_forwarding', [...(ws.email_forwarding || []), { alias: '', forward_to: '' }])}
                className="border-white/20 text-white bg-white/5 rounded-xl" data-testid="add-email-forward-btn">
                <Plus className="w-4 h-4 mr-1" />Add Email
              </Button>
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3 mt-2">
                <p className="text-amber-300 text-xs">Email forwarding requires DNS setup on the djcrm.net domain. Once you save, the admin will configure the forwarding rules for your emails.</p>
              </div>
            </div>
            <Button onClick={() => save({ email_forwarding: ws.email_forwarding })} disabled={saving} className="w-full h-12 rounded-xl font-semibold bg-blue-500 hover:bg-blue-600" data-testid="save-email-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Save Email Settings
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
